import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import BranchEmployeesTab from "./components/BranchEmployeesTab";
import BranchHoursTab from "./components/BranchHoursTab";
import BranchInfoTab from "./components/BranchInfoTab";

type Tab = "info" | "hours" | "employees";

export default function BranchDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { branches, employees } = useStore();
  const [tab, setTab] = useState<Tab>("info");

  const branch = useMemo(
    () => branches.find((item) => item.id === id) ?? null,
    [branches, id],
  );

  const branchEmployees = useMemo(
    () => employees.filter((item) => item.branchId === id),
    [employees, id],
  );

  const managerName = useMemo(() => {
    const manager = branch?.managerId
      ? branchEmployees.find((item) => item.id === branch.managerId)
      : branchEmployees.find((item) => item.role === "branchManager");
    return manager?.name ?? "";
  }, [branchEmployees, branch?.managerId]);

  if (!branch) {
    return (
      <div className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card px-8 py-20 text-center">
        <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
          <i className="ri-store-3-line text-3xl"></i>
        </div>
        <h2 className="text-lg font-bold text-foreground-950">
          الفرع غير موجود
        </h2>
        <p className="mt-1 text-sm text-foreground-500">
          تعذّر العثور على الفرع المطلوب، ربما تم حذفه.
        </p>
        <button
          type="button"
          onClick={() => navigate("/branches")}
          className="mt-5 inline-flex items-center gap-2 rounded-btn bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-right-line"></i>
          العودة للفروع
        </button>
      </div>
    );
  }

  const tabs: { key: Tab; label: string; icon: string; count?: number }[] = [
    { key: "info", label: "بيانات الفرع", icon: "ri-store-2-line" },
    { key: "hours", label: "أوقات العمل والاستلام", icon: "ri-time-line" },
    {
      key: "employees",
      label: "الموظفون",
      icon: "ri-team-line",
      count: branchEmployees.length,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="yc-fade-up flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/branches")}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
            aria-label="رجوع"
          >
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-bold text-foreground-950">
                {branch.name}
              </h2>
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${
                  !branch.isActive
                    ? "bg-red-100 text-red-600"
                    : branch.isOpen
                      ? "bg-primary-100 text-primary-800"
                      : "bg-secondary-200 text-secondary-800"
                }`}
              >
                {!branch.isActive
                  ? "معطّل"
                  : branch.isOpen
                    ? "مفتوح"
                    : "مغلق"}
              </span>
            </div>
            <p className="mt-0.5 text-sm text-foreground-500">{branch.city}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setTab("employees")}
            className="inline-flex items-center justify-center gap-2 h-11 rounded-btn bg-accent-100 px-4 text-sm font-semibold text-accent-900 hover:bg-accent-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-team-line"></i>
            إدارة موظفي الفرع
          </button>
          <button
            type="button"
            onClick={() => navigate(`/branches/${branch.id}/edit`)}
            className="inline-flex items-center justify-center gap-2 h-11 rounded-btn bg-primary-500 px-5 text-sm font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line"></i>
            تعديل الفرع
          </button>
        </div>
      </div>

      <div className="yc-fade-up inline-flex flex-wrap gap-1 rounded-full bg-white border border-background-200 p-1">
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap ${
              tab === item.key
                ? "bg-primary-500 text-white"
                : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            <i className={item.icon}></i>
            {item.label}
            {typeof item.count === "number" && (
              <span
                className={`rounded-full px-1.5 text-[11px] font-bold ${
                  tab === item.key
                    ? "bg-white/25 text-white"
                    : "bg-background-100 text-foreground-500"
                }`}
              >
                {item.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <section className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5">
        {tab === "info" && (
          <BranchInfoTab
            branch={branch}
            employeesCount={branchEmployees.length}
            managerName={managerName || "لا يوجد مدير فرع مضاف"}
          />
        )}
        {tab === "hours" && <BranchHoursTab branch={branch} />}
        {tab === "employees" && (
          <BranchEmployeesTab
            branchId={branch.id}
            employees={branchEmployees}
          />
        )}
      </section>
    </div>
  );
}